import { FileMeta } from '/imports/types';
import { DeclareMethod } from '/imports/methods';
import { formatBytes, uInt8ToBase64 } from '/imports/utils';
import { strings } from '/imports/strings';

/**
 * Browser URL for a stored file, carrying its capability token so the /nfs auth gate can authorize the
 * fetch (browser <img>/<a> GETs send no session). Use this for EVERY href/src pointing at a stored file
 * instead of raw file.url. Passes non-/nfs urls and tokenless legacy files through unchanged.
 * @param file - the file metadata (url + token)
 * @returns the tokenized URL, or undefined when there is no file
 */
export const fileHref = (file?: { url?: string, token?: string } | null, downloadName?: string): string | undefined => {
  if (!file?.url) return undefined;
  // A stored file is named by its content hash, so a browser saving it produces something meaningless.
  // The anchor's own `download` attribute cannot fix that — the server sends a Content-Disposition, which
  // wins — so the wanted name rides along and /nfs puts it in that header. It sits outside the signed
  // token on purpose: it names the copy being saved and grants nothing.
  const name = downloadName?.trim() ? `name=${encodeURIComponent(downloadName.trim())}` : '';
  const jwt = file.token && file.url.startsWith('/nfs') ? `jwt=${encodeURIComponent(file.token)}` : '';
  const query = [jwt, name].filter(Boolean).join('&');
  if (!query) return file.url;
  return `${file.url}${file.url.includes('?') ? '&' : '?'}${query}`;
};

// --- Mint-on-demand short-TTL file tokens (DORMANT / opt-in alternative to the static fileHref above) ---
// The default posture is static / never-expire. This is the isolated, not-yet-wired path for short-lived,
// per-access tokens (e.g. a sensitive doctype that needs a bounded leak window). No consumer uses it yet,
// and no server policy is registered, so mintFileTokens rejects everything until one is added.

/** Mint short-TTL capability tokens for files under a named mint-on-demand policy. Returns url→token for
 *  authorized files only. DORMANT: rejects with 'forbidden' until a policy is registered server-side. */
export const mintFileTokens = DeclareMethod<{ urls: string[], policy: string }, { [url: string]: string }>('mintFileTokens');

const appendJwt = (url: string, token: string): string =>
  `${url}${url.includes('?') ? '&' : '?'}jwt=${encodeURIComponent(token)}`;

/** Mint-on-demand tokenized URL for one file (async round-trip). Returns undefined if the caller isn't
 *  authorized for it or the policy doesn't exist. Prefer mintedFileHrefs for lists (one round-trip). */
export const mintedFileHref = async (url: string, policy: string): Promise<string | undefined> => {
  const map = await mintFileTokens.call({ urls: [url], policy });
  return map[url] ? appendJwt(url, map[url]) : undefined;
};

/** Batch mint-on-demand: one round-trip for many files. Returns url→tokenizedUrl; unauthorized files are
 *  omitted. Use for galleries/tables where <img>/<a> can't await a per-file mint. */
export const mintedFileHrefs = async (urls: string[], policy: string): Promise<{ [url: string]: string }> => {
  const map = await mintFileTokens.call({ urls, policy });
  const out: { [url: string]: string } = {};
  for (const [url, token] of Object.entries(map)) out[url] = appendJwt(url, token);
  return out;
};

/** Hard ceiling on any single upload. Nothing can raise it; a surface may only ask for less. */
export const maxUploadBytes = 2 * 1024 * 1024 * 1024;

// Per-surface ceilings, set where the content is a known, bounded format. Surfaces that take "any
// document" — the data room, task attachments, document versions, support tickets — deliberately keep the
// global ceiling, because a legitimately huge file there is nobody's mistake.
/** Source image handed to a cropper. The cropped blob that actually uploads is a fraction of this. */
export const maxImageUploadBytes = 25 * 1024 * 1024;
/** Workbook parsed server-side by ExcelJS. */
export const maxSpreadsheetUploadBytes = 25 * 1024 * 1024;
/** Line-delimited address or site list read into memory. */
export const maxTextImportBytes = 10 * 1024 * 1024;
/** A single scanned invoice. */
export const maxInvoiceUploadBytes = 50 * 1024 * 1024;

/**
 * Resolves the byte ceiling for one upload. Clamping a surface's own limit — rather than trusting it —
 * is what keeps the global cap the real one no matter what a client sends.
 * @param maxSize - the surface's requested ceiling, if it set one
 * @returns the ceiling to enforce, in bytes
 */
export const uploadLimitBytes = (maxSize?: number): number =>
  maxSize && maxSize > 0 ? Math.min(maxSize, maxUploadBytes) : maxUploadBytes;

// --- Executables are refused by default ---------------------------------------------------------------
// A Windows program (.exe) is the one file type no surface takes unless it says so twice: the widget's
// `accept` names it, and the upload starts through startExecutableFileUpload, which the server guards on
// its own. The server also reads the bytes, so a program renamed to .pdf is refused like one named .exe.

/** Extensions refused unless explicitly accepted. */
export const executableUploadExtensions: readonly string[] = ['exe'];
/** MIME types browsers and servers give a Windows program. */
export const executableUploadTypes: readonly string[] = [
  'application/x-msdownload', 'application/vnd.microsoft.portable-executable', 'application/x-dosexec',
  'application/x-ms-dos-executable', 'application/x-msdos-program', 'application/exe', 'application/x-exe', 'application/x-winexe',
];

/**
 * Whether a file's name or declared type says it is a program. Trailing dots and spaces are dropped first,
 * because Windows ignores them: "setup.exe." runs as setup.exe.
 * @param file - the file's name and declared MIME type
 * @returns true for a program
 */
export const isExecutableUpload = (file: { name?: string, type?: string }): boolean => {
  const name = (file.name || '').trim().replace(/[.\s]+$/, '').toLowerCase();
  const dot = name.lastIndexOf('.');
  const extension = dot >= 0 ? name.slice(dot + 1) : '';
  const type = (file.type || '').split(';')[0].trim().toLowerCase();
  return executableUploadExtensions.includes(extension) || executableUploadTypes.includes(type);
};

/**
 * Whether an `accept` attribute explicitly lets programs in. Only a named token counts: no accept at all,
 * a wildcard, or "application/*" still refuses them.
 * @param accept - the widget's accept attribute
 * @returns true when it names .exe or a program MIME type
 */
export const acceptAllowsExecutable = (accept?: string): boolean =>
  !!accept && accept.split(',').map((token) => token.trim().toLowerCase())
    .some((token) => (token.startsWith('.') && executableUploadExtensions.includes(token.slice(1))) || executableUploadTypes.includes(token));

/** Begin a chunked upload; returns a token used for subsequent chunks. Refuses programs. */
export const startFileUpload = DeclareMethod<{ name: string, type: string, size: number, maxSize?: number }, string>('startFileUpload');
/** Begin a chunked upload that may be a program. Guarded separately on the server; use only from a widget
 *  whose accept names .exe (startUploadMethodFor picks it). */
export const startExecutableFileUpload = DeclareMethod<{ name: string, type: string, size: number, maxSize?: number }, string>('startExecutableFileUpload');

/**
 * The start method an upload from a widget with this accept attribute should use.
 * @param accept - the widget's accept attribute
 * @returns startExecutableFileUpload when accept names programs, startFileUpload otherwise
 */
export const startUploadMethodFor = (accept?: string) => (acceptAllowsExecutable(accept) ? startExecutableFileUpload : startFileUpload);

/**
 * The refusal a widget shows before uploading anything, when the file is a program its accept does not name.
 * @param file - the picked file
 * @param accept - the widget's accept attribute
 * @returns the message to show, or undefined when the file may go up
 */
export const executableUploadRefusal = (file: { name: string, type?: string }, accept?: string): string | undefined =>
  (isExecutableUpload(file) && !acceptAllowsExecutable(accept) ? strings.ExecutableUploadBlocked(file.name) : undefined);
/** Append one base64 chunk to an in-progress upload. */
export const uploadFileChunk = DeclareMethod<{ token: string, chunk: string }>('uploadFileChunk');
/** Finalize an upload and return the stored file's metadata. */
export const finishFileUpload = DeclareMethod<string, FileMeta>('finishFileUpload');

/**
 * Uploads a browser File through the chunked-upload methods (headless counterpart of FileInput,
 * for programmatic/batch flows).
 * @param file - the browser File to upload
 * @param onProgress - optional 0–100 progress callback
 * @param maxSize - optional per-surface byte ceiling, clamped server-side to maxUploadBytes
 * @param accept - optional accept list, read only to decide whether a program may go up (see acceptAllowsExecutable)
 * @returns the stored file's metadata
 */
export const uploadFile = async (file: File, onProgress?: (pct: number) => void, maxSize?: number, accept?: string): Promise<FileMeta> => {
  const { name, type, size } = file;
  const limit = uploadLimitBytes(maxSize);
  if (size > limit) throw new Error(strings.FileTooLarge(name, formatBytes(size), formatBytes(limit)));
  const refusal = executableUploadRefusal(file, accept);
  if (refusal) throw new Error(refusal);
  const token = await startUploadMethodFor(accept).call({ name, type, size, maxSize });
  const reader = (file.stream() as any).getReader();
  let uploadedBytes = 0;
  let read;
  while ((read = await reader.read())) {
    if (read.value) {
      await uploadFileChunk.call({ token, chunk: uInt8ToBase64(read.value) });
      uploadedBytes += read.value.byteLength;
      onProgress?.(Math.round((uploadedBytes / size) * 100));
    }
    if (read.done) break;
  }
  return await finishFileUpload.call(token);
};
